/**
 * overlay_scroll_guard_controller.js — a drag on a foreground overlay never scrolls the page.
 *
 * Mounted once on <body> (templates/base.html), so it covers every overlay that
 * exists and every one added later: Bootstrap modals and offcanvases (including
 * the non-modal ones opened with data-bs-scroll="true", which take no page lock
 * at all), bottom sheets, the photo lightbox, the notifications panel, the chat
 * panel, card menus.
 *
 * The rule: a touch that starts inside a `position: fixed` element is a touch on
 * the foreground. A fixed element does not move with the document, so a drag on
 * it never has a reason to scroll the document. The drag may scroll something
 * INSIDE the overlay that can still move in that direction (a long list, a
 * horizontal photo strip, a textarea); otherwise it is cancelled.
 *
 * Why this is not CSS. `overscroll-behavior: contain` only acts on an element
 * that actually overflows: a sheet whose content fits, or a drag on its header
 * or grab handle, has no scroller to contain it and the gesture falls through
 * to the page. The modal lock in static/css/mobile.css (html:has(> body.modal-open))
 * holds the page still only while a full modal is open, and a non-modal panel
 * never sets modal-open.
 *
 * Each touchmove is judged while it can still be cancelled. Browsers latch a
 * gesture onto one scroller once it starts moving and later moves arrive
 * uncancelable, so an inner list scrolled to its end does not hand the rest of
 * that same gesture to the page.
 *
 * The touchmove listener must be non-passive to cancel anything. It returns at
 * once unless the touch began on an overlay, so a drag on the page itself costs
 * one property read.
 */

import { Controller } from "@hotwired/stimulus";

/** Controls whose own drag behaviour a cancelled touchmove would break. */
const NATIVE_DRAG = 'input[type="range"]';

export default class extends Controller {
    connect() {
        this._overlay = null;
        this._start = null;
        this._onStart = this.start.bind(this);
        this._onMove = this.move.bind(this);
        this._onEnd = this.end.bind(this);
        document.addEventListener("touchstart", this._onStart, { capture: true, passive: true });
        document.addEventListener("touchmove", this._onMove, { capture: true, passive: false });
        document.addEventListener("touchend", this._onEnd, { capture: true, passive: true });
        document.addEventListener("touchcancel", this._onEnd, { capture: true, passive: true });
    }

    disconnect() {
        document.removeEventListener("touchstart", this._onStart, { capture: true });
        document.removeEventListener("touchmove", this._onMove, { capture: true });
        document.removeEventListener("touchend", this._onEnd, { capture: true });
        document.removeEventListener("touchcancel", this._onEnd, { capture: true });
    }

    start(event) {
        const touch = event.touches.length === 1 ? event.touches[0] : null;
        const target = event.target instanceof Element ? event.target : null;
        this._overlay = touch && target && !target.closest(NATIVE_DRAG) ? fixedAncestor(target) : null;
        this._start = touch && { x: touch.clientX, y: touch.clientY };
    }

    move(event) {
        if (!this._overlay || !event.cancelable || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dx = this._start.x - touch.clientX;
        const dy = this._start.y - touch.clientY;
        if (dx === 0 && dy === 0) return;
        if (!canScrollWithin(event.target, this._overlay, dx, dy)) event.preventDefault();
    }

    end() {
        this._overlay = null;
        this._start = null;
    }
}

/** The nearest `position: fixed` element containing el, or null for page content. */
function fixedAncestor(el) {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
        if (getComputedStyle(node).position === "fixed") return node;
    }
    return null;
}

/**
 * Whether anything from el up to the overlay root can scroll the way the finger
 * is moving. dx/dy are the content's scroll direction: positive dy scrolls down.
 * The dominant axis decides, so a slightly diagonal vertical drag is vertical.
 */
function canScrollWithin(el, root, dx, dy) {
    const vertical = Math.abs(dy) >= Math.abs(dx);
    for (let node = el; node; node = node.parentElement) {
        if (vertical ? canScrollY(node, dy) : canScrollX(node, dx)) return true;
        if (node === root) break;
    }
    return false;
}

function canScrollY(node, dy) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow !== "auto" && overflow !== "scroll") return false;
    if (dy > 0) return node.scrollTop + node.clientHeight < node.scrollHeight - 1;
    return node.scrollTop > 0;
}

function canScrollX(node, dx) {
    const overflow = getComputedStyle(node).overflowX;
    if (overflow !== "auto" && overflow !== "scroll") return false;
    const max = node.scrollWidth - node.clientWidth;
    // Right-to-left scrollers report scrollLeft from 0 down to -max.
    const left = Math.abs(node.scrollLeft);
    if (dx > 0) return left < max - 1;
    return left > 0;
}
